/**
 * Google Analytics 4 Service
 * Centralized analytics tracking service for Nova UI
 *
 * Privacy Notes:
 * - Never sends PII (emails, names, phone numbers) to GA4
 * - Uses Firebase UID for user identification (not email)
 * - Respects GDPR consent settings
 */

import type { UserProperties, EventParams, ConsentState } from './types';

// Configuration
const GA_MEASUREMENT_ID = process.env.REACT_APP_MEASUREMENT_ID || '';
const DEBUG_MODE = process.env.NODE_ENV === 'development';

// Storage key for consent preference
const CONSENT_STORAGE_KEY = 'nova_analytics_consent';

// Activation tracking keys (to track first-time events)
const ACTIVATION_STORAGE_PREFIX = 'nova_activation_';

/**
 * Check if analytics is properly initialized
 */
const isAnalyticsReady = (): boolean => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Check if user has granted analytics consent
 */
export const hasAnalyticsConsent = (): boolean => {
  if (typeof window === 'undefined') return false;
  const consent = localStorage.getItem(CONSENT_STORAGE_KEY);
  return consent === 'granted';
};

/**
 * Get stored consent state
 */
export const getStoredConsent = (): 'granted' | 'denied' | null => {
  if (typeof window === 'undefined') return null;
  const consent = localStorage.getItem(CONSENT_STORAGE_KEY);
  if (consent === 'granted' || consent === 'denied') {
    return consent;
  }
  return null;
};

/**
 * Update consent state
 * Should be called when user interacts with cookie consent banner
 */
export const updateConsent = (granted: boolean): void => {
  if (!isAnalyticsReady()) {
    console.warn('[Analytics] gtag not ready for consent update');
    return;
  }

  const consentState: ConsentState = {
    analytics_storage: granted ? 'granted' : 'denied',
    ad_storage: 'denied', // Always deny ad storage
  };

  // Store preference
  localStorage.setItem(CONSENT_STORAGE_KEY, granted ? 'granted' : 'denied');

  // Update GA4 consent
  window.gtag('consent', 'update', consentState);

  if (DEBUG_MODE) {
    console.log('[Analytics] Consent updated:', consentState);
  }
};

/**
 * Initialize GA4 with stored consent
 * Called after gtag script loads
 */
export const initializeAnalytics = (): void => {
  if (!isAnalyticsReady() || !GA_MEASUREMENT_ID) {
    if (DEBUG_MODE) {
      console.warn('[Analytics] gtag not ready or measurement ID missing');
    }
    return;
  }

  // Check for stored consent and apply it
  const storedConsent = getStoredConsent();
  if (storedConsent === 'granted') {
    window.gtag('consent', 'update', {
      analytics_storage: 'granted',
      ad_storage: 'denied',
    });
  }

  // Configure GA4
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false, // We'll handle page views manually
    debug_mode: DEBUG_MODE,
  });

  if (DEBUG_MODE) {
    console.log('[Analytics] Initialized with measurement ID:', GA_MEASUREMENT_ID);
  }
};

/**
 * Track a page view
 * @param pagePath - The page path (e.g., '/dashboard')
 * @param pageTitle - Optional page title
 */
export const trackPageView = (pagePath: string, pageTitle?: string): void => {
  if (!isAnalyticsReady() || !hasAnalyticsConsent()) {
    if (DEBUG_MODE) {
      console.log('[Analytics] Page view skipped (no consent or not ready):', pagePath);
    }
    return;
  }

  window.gtag('event', 'page_view', {
    page_path: pagePath,
    page_title: pageTitle || document.title,
    page_location: window.location.href,
  });

  if (DEBUG_MODE) {
    console.log('[Analytics] Page view:', pagePath);
  }
};

/**
 * Track a custom event
 * @param eventName - The event name (use constants from events.ts)
 * @param params - Optional event parameters
 */
export const trackEvent = (eventName: string, params?: EventParams): void => {
  if (!isAnalyticsReady() || !hasAnalyticsConsent()) {
    if (DEBUG_MODE) {
      console.log('[Analytics] Event skipped (no consent or not ready):', eventName, params);
    }
    return;
  }

  window.gtag('event', eventName, params);

  if (DEBUG_MODE) {
    console.log('[Analytics] Event:', eventName, params);
  }
};

/**
 * Set user ID for cross-device tracking
 * @param userId - Firebase UID (NOT email or PII)
 */
export const setUserId = (userId: string): void => {
  if (!isAnalyticsReady()) {
    if (DEBUG_MODE) {
      console.warn('[Analytics] gtag not ready for setUserId');
    }
    return;
  }

  // Set user ID
  window.gtag('config', GA_MEASUREMENT_ID, {
    user_id: userId,
  });

  if (DEBUG_MODE) {
    console.log('[Analytics] User ID set:', userId.substring(0, 8) + '...');
  }
};

/**
 * Clear user ID on logout
 */
export const clearUserId = (): void => {
  if (!isAnalyticsReady()) return;

  window.gtag('config', GA_MEASUREMENT_ID, {
    user_id: undefined,
  });

  if (DEBUG_MODE) {
    console.log('[Analytics] User ID cleared');
  }
};

/**
 * Set user properties for segmentation
 * @param properties - User properties (no PII!)
 */
export const setUserProperties = (properties: UserProperties): void => {
  if (!isAnalyticsReady() || !hasAnalyticsConsent()) {
    if (DEBUG_MODE) {
      console.log('[Analytics] User properties skipped (no consent or not ready):', properties);
    }
    return;
  }

  window.gtag('set', 'user_properties', properties);

  if (DEBUG_MODE) {
    console.log('[Analytics] User properties set:', properties);
  }
};

/**
 * Track activation milestone (first-time events)
 * Only fires once per user
 * @param eventName - The activation event name
 * @param userId - User ID to track milestone
 */
export const trackActivationMilestone = (eventName: string, userId: string): void => {
  const storageKey = `${ACTIVATION_STORAGE_PREFIX}${eventName}_${userId}`;

  // Check if already tracked
  if (localStorage.getItem(storageKey)) {
    return;
  }

  // Track the event
  trackEvent(eventName);

  // Mark as tracked
  localStorage.setItem(storageKey, new Date().toISOString());

  if (DEBUG_MODE) {
    console.log('[Analytics] Activation milestone:', eventName);
  }
};

/**
 * Track an error event
 * @param errorType - Type of error (api_error, page_error)
 * @param details - Error details
 */
export const trackError = (
  errorType: 'api_error' | 'page_error',
  details: {
    endpoint?: string;
    status_code?: number;
    error_message?: string;
    page?: string;
  }
): void => {
  // Sanitize error message to remove any potential PII
  const sanitizedMessage = details.error_message
    ? details.error_message.replace(/[\w.-]+@[\w.-]+/g, '[EMAIL]') // Remove emails
        .replace(/\b\d{10,}\b/g, '[PHONE]') // Remove phone numbers
        .substring(0, 100) // Limit length
    : undefined;

  trackEvent(errorType, {
    ...details,
    error_message: sanitizedMessage,
  });
};

/**
 * Export analytics service as default object
 */
const analyticsService = {
  initializeAnalytics,
  hasAnalyticsConsent,
  getStoredConsent,
  updateConsent,
  trackPageView,
  trackEvent,
  setUserId,
  clearUserId,
  setUserProperties,
  trackActivationMilestone,
  trackError,
};

export default analyticsService;
